import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ShieldCheck, Lock, Eye, MessageSquare, ArrowRight,
  Brain, Smile, HeartHandshake, Gift, Unlock, Upload,
} from 'lucide-react';
import { Container, SectionHeading, Btn, Card, Pill } from '../components/ui';
import Reveal from '../components/Reveal';
import FAQAccordion from '../components/FAQAccordion';
import CTASection from '../components/CTASection';
import { FeatureCard, CheckList } from '../components/cards';
import AstridHero from '../components/astrid-hero/AstridHero';
import AstridDrawer from '../components/AstridDrawer';
import Seo from '../components/Seo';
import { faqJsonLd } from '../data/seo';

const CAPABILITIES = [
  { icon: 'Search', title: 'Ask across your operations', text: 'Find bills, accounts, vendors, properties, payments, notes and exceptions in plain English — no report name or navigation path required.' },
  { icon: 'AlertTriangle', title: 'Investigate what does not match', text: 'Trace overdue and missing bills, payment discrepancies and vendor-level patterns without jumping between screens.' },
  { icon: 'FileSpreadsheet', title: 'Turn answers into reports', text: 'Create CSV, Excel and branded sustainability PDF outputs from data you are permitted to access.' },
  { icon: 'Mail', title: 'Prepare vendor outreach', text: 'Draft one email or a batch of follow-ups, then review and approve before anything is sent.' },
  { icon: 'Rocket', title: 'Guide complex onboarding', text: 'Work through collector setup, utility-platform migration, missing-bill configuration and ENERGY STAR readiness one step at a time.' },
  { icon: 'Leaf', title: 'Prioritize sustainability work', text: 'Identify high-impact properties, recommend emissions targets and generate portfolio reporting.' },
];

const HOW = [
  { n: '01', title: 'You ask naturally', text: 'Type a normal business question — no schema, report name or filter syntax needed.' },
  { n: '02', title: 'Astrid selects approved tools', text: 'Astrid interprets the intent and chooses a purpose-built BillPortal capability.' },
  { n: '03', title: 'Your permissions are applied', text: 'BillPortal enforces your organization and role scope before any data is retrieved.' },
  { n: '04', title: 'You get evidence and a next step', text: 'A concise answer with sources, plus a report, draft, link or guided next action.' },
  { n: '05', title: 'You review before it matters', text: 'Supported write workflows show a preview and require your explicit confirmation.' },
];

const USE_CASES = {
  Onboarding: [
    'Help me onboard these utility portal credentials.',
    'Which rows in my file need to be fixed?',
    'Can you help us move from our current platform?',
    'Set up missing bills for this organization.',
  ],
  Operations: [
    'Which vendors need my attention this week?',
    'Show me the highest balances for Cardinal this month.',
    'Which expected bills are missing?',
    'Create a CSV of overdue electric bills by property.',
    'Where can I find the demand report?',
  ],
  Payments: [
    "Why doesn't this payment file match the bill total?",
    'Reconcile our fund pulls against payment files.',
    'Compare year-to-date payment files with the credit/debit report.',
    'Which payments need investigation?',
  ],
  Sustainability: [
    'Which properties should we prioritize for decarbonization?',
    'Recommend a 2030 emissions target.',
    'Create a branded sustainability PDF.',
    'Which properties are ready to connect to ENERGY STAR?',
  ],
};

const TRUST = [
  { icon: Lock, title: 'Role-based & organization-scoped', text: 'Astrid inherits your organization and role scope. Its data tools are limited to permitted collections and fields — it cannot see everything.' },
  { icon: Eye, title: 'Grounded at answer time', text: 'Astrid is not custom-trained on your private data. At answer time it combines your question with approved tools that retrieve current, permission-scoped data.' },
  { icon: ShieldCheck, title: 'Review before action', text: 'General operations are read-only by default. Sensitive supported workflows use preview and confirmation gates — nothing consequential happens without you.' },
];

const FAQ = [
  { q: 'Is Astrid trained on my data?', a: 'No. Astrid is not custom-trained on your private company data. At answer time, BillPortal provides relevant context and permission-controlled tools that can retrieve current workspace data for your request.' },
  { q: 'Can Astrid see everything in BillPortal?', a: 'No. Astrid inherits your organization and role scope. Its data tools are limited to permitted collections and fields.' },
  { q: 'Can Astrid make changes for me?', a: 'Most Astrid interactions are read-only. Specific guided workflows — such as collector onboarding, migration, missing-bill setup and some sustainability setup — can prepare or apply supported changes only through defined previews and confirmation steps.' },
  { q: 'Can Astrid send vendor emails?', a: 'Astrid can draft individual and batch vendor emails. The product includes an approval workflow so a person can inspect the content before delivery.' },
  { q: 'What can I ask Astrid?', a: 'Ask about bills, vendors, properties, payment discrepancies, missing bills, reports, files, migrations, portal collectors, sustainability, ENERGY STAR readiness, notes, tasks, or where to find something in BillPortal.' },
  { q: 'Does Astrid replace BillPortal reports?', a: 'No. Astrid can find and link to existing reports, explain their results, or generate a custom export when appropriate.' },
  { q: 'Is every answer guaranteed to be accurate?', a: 'No AI-assisted answer is guaranteed. Astrid is in Beta. Always review dates, amounts, account numbers and external commitments before acting or sharing.' },
  { q: 'How is Astrid priced?', a: 'Applicable Astrid activity uses your organization’s AI-credit wallet. AI credits are separate from the bill credits used for bill collection and parsing, and are non-refundable — so use them wisely.' },
  { q: 'Can I upload files?', a: 'Yes. Astrid includes chat attachments and an Astrid Drive area. Files can support document analysis, collector onboarding, migration imports and guided configuration workflows.' },
  { q: 'Does Astrid remember the conversation?', a: 'Conversations are persisted and can be revisited. The drawer includes a conversation list and supports continuing prior work.' },
];

export default function Astrid() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [seedQuestion, setSeedQuestion] = useState(null);
  const [tab, setTab] = useState('Onboarding');

  const openDrawer = (q) => { setSeedQuestion(q || null); setDrawerOpen(true); };

  return (
    <div data-testid="astrid-page">
      <Seo
        title="Meet Astrid — Your AI Support Agent for Utility Operations"
        description="Astrid is BillPortal's conversational utility-operations specialist. Ask about bills, vendors, payments, migrations and sustainability — grounded in permission-scoped BillPortal data, with review before action."
        keywords="Astrid AI agent, BillPortal AI assistant, utility operations AI, AI bill assistant, AI support agent"
        jsonLd={faqJsonLd(FAQ.map((f) => ({ q: f.q, a: f.a })))}
      />

      {/* HERO — immersive 3D intelligence layer */}
      <AstridHero onAsk={openDrawer} />

      {/* WHAT ASTRID IS */}
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container>
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <SectionHeading align="left" eyebrow="What Astrid is" title="An AI teammate & orchestration layer — not just a chatbot" />
            </Reveal>
            <Reveal className="lg:col-span-7">
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
                You tell Astrid the outcome you need. It understands your organisation and the BillPortal platform
                around you, investigates the data, creates reports and operational files, composes and receives vendor
                communications, helps configure workflows, guides your team through the platform, initiates actions —
                and brings sensitive decisions back to you for approval. It works <strong>inside</strong> your utility
                operation, not beside it.
              </p>
              <CheckList
                className="mt-6"
                items={[
                  'Understands your organisation and the BillPortal platform around you.',
                  'Creates operational artifacts — reports, files, setups and vendor drafts.',
                  'Coordinates large-scale work (e.g. ACH outreach to every vendor) from one instruction.',
                  'Human-in-the-loop by design — sensitive actions come back to you for approval.',
                ]}
              />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* WHY WE BUILT ASTRID */}
      <section className="border-y border-slate-200 bg-slate-50 px-5 py-16 dark:border-white/10 dark:bg-white/[0.02] sm:px-8 sm:py-20">
        <Container>
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <SectionHeading align="left" eyebrow="Why we built Astrid" title="Utility bill management is exhausting. Astrid lends a hand." />
            </Reveal>
            <Reveal className="lg:col-span-7">
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
                Chasing information, reconciling exceptions and following up with teams is painful — and even
                well-resourced teams feel the hollowness of carrying it alone. We built Astrid to give every person on
                your team an extra pair of hands: to help them come up to speed, cut the busywork, and get answers
                without the chase.
              </p>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand"><Brain className="h-4.5 w-4.5" /></span>
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300"><strong className="text-slate-900 dark:text-white">A brain that drives the platform.</strong> Astrid's engine does the heavy lifting; we've simply wrapped it in a familiar conversation so anyone can just ask.</p>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand"><Smile className="h-4.5 w-4.5" /></span>
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300"><strong className="text-slate-900 dark:text-white">And a little personality.</strong> Astrid has a sense of humour and will crack the odd joke while it works — because the work is serious enough already.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="scroll-mt-20 bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Capabilities" title="Everything you can hand to Astrid" subtitle="Six things Astrid does today, grounded in the work already happening in BillPortal." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (<Reveal key={c.title} delay={i * 60}><FeatureCard icon={c.icon} title={c.title} text={c.text} /></Reveal>))}
          </div>

          {/* CTA — register & test Astrid with your own bills */}
          <Reveal>
            <div className="mt-10 flex flex-col items-center gap-5 rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/[0.06] to-transparent p-8 text-center dark:border-brand/25 sm:p-10" data-testid="astrid-capabilities-cta">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Upload className="h-6 w-6" /></span>
              <div>
                <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl">Put Astrid to the test with your own bills</h3>
                <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-slate-600 dark:text-slate-300 sm:text-base">
                  Register free, upload a few sample utility bills or onboard your UBM data, and watch Astrid investigate,
                  create and act on it — no commitment, and free AI credits to start.
                </p>
              </div>
              <Btn
                href="https://app.billportal.ai/signup?src=astrid"
                target="_blank"
                rel="noopener noreferrer"
                size="lg"
                data-testid="astrid-register-test-cta"
              >
                Register &amp; test Astrid <ArrowRight className="h-4 w-4" />
              </Btn>
              <p className="text-xs text-slate-400 dark:text-slate-500">Takes a minute · your data stays permission-scoped to your organisation</p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* USE CASES */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Use cases" title="Ask it the way you'd ask a teammate" subtitle="Real questions across the teams Astrid supports. Pick one to see how Astrid responds." /></Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-2" data-testid="astrid-usecase-tabs">
            {Object.keys(USE_CASES).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                data-testid={`astrid-tab-${k.toLowerCase()}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? 'bg-brand text-white' : 'bg-slate-100 text-slate-600 hover:text-brand dark:bg-white/5 dark:text-slate-300'}`}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-3 sm:grid-cols-2">
            {USE_CASES[tab].map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => openDrawer(q)}
                data-testid="astrid-usecase-question"
                className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left text-sm font-medium text-slate-700 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200"
              >
                <span className="flex items-center gap-2.5"><MessageSquare className="h-4 w-4 shrink-0 text-brand" /> {q}</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-brand" />
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="How it works" title="Ask → permission check → tool → answer → review" /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {HOW.map((s, i) => (
              <Reveal key={s.n} delay={i * 60}>
                <Card className="h-full">
                  <div className="font-mono text-sm font-bold text-brand">{s.n}</div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{s.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* TRUST & BETA */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Trust & boundaries" title="Powerful, but controlled by default" subtitle="Astrid is powered by leading language models and grounded in your workspace — with guardrails you can rely on." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {TRUST.map((t, i) => (
              <Reveal key={t.title} delay={i * 60}>
                <Card className="h-full">
                  <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <t.icon className="h-5 w-5" />
                  </span>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{t.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>

          {/* Beta notice */}
          <Reveal>
            <div className="mt-8 flex flex-col items-start gap-4 rounded-2xl border border-amber-300/60 bg-amber-50 p-6 dark:border-amber-500/30 dark:bg-amber-500/[0.06] sm:flex-row sm:items-center" data-testid="astrid-beta-notice">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400/20 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <div className="text-sm font-bold text-amber-900 dark:text-amber-200">We'd rather give you transparency than paint a picture</div>
                <p className="mt-1 text-sm leading-relaxed text-amber-800/90 dark:text-amber-200/80">
                  Astrid can do remarkable things and unlock real efficiency — but it's early, so we ask you to test it,
                  confirm the results, and share your feedback so it keeps getting better. AI-assisted output depends on
                  the prompts you give it and can contain inaccuracies; verify important dates, amounts, account numbers
                  and commitments before acting or sharing. Astrid is in Beta and provided without warranties on its actions.
                </p>
              </div>
              <Link to="/legal/astrid-terms" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600" data-testid="astrid-beta-terms-link">
                Read Astrid terms <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* AI CREDITS */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-20">
        <Container>
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <SectionHeading align="left" eyebrow="AI credits" title="Free credits to start — pay only for what you use" />
              <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-slate-300">
                Because we want you to feel comfortable exploring, <strong>Astrid credits are included free</strong> on
                every free registration and on all paid plans — so you can play, test and see the value before you spend
                a cent. There's <strong>no lock-in and no forced subscription</strong>: you pay only for what you use.
                This is an early phase and Astrid will keep evolving. AI credits are prepaid and non-refundable once
                used (BillPortal incurs model and infrastructure costs per action), so spend them thoughtfully.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Pill><Gift className="h-3.5 w-3.5 text-brand" /> Free credits on every plan</Pill>
                <Pill><Unlock className="h-3.5 w-3.5 text-brand" /> No lock-in — pay per use</Pill>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <Card className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand"><HeartHandshake className="h-5 w-5" /></span>
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Fair by design</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">How Astrid credits work</div>
                  </div>
                </div>
                <CheckList
                  items={[
                    'Free Astrid credits on free registrations and all paid plans.',
                    'No subscription lock-in — you only pay for what you use.',
                    'AI credits and bill credits are metered separately.',
                    'Prepaid credits are non-refundable once used; low-balance warnings appear in Astrid.',
                  ]}
                />
              </Card>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container>
          <Reveal><SectionHeading eyebrow="FAQ" title="Frequently asked questions" /></Reveal>
          <Reveal delay={80} className="mt-10"><FAQAccordion items={FAQ} /></Reveal>

          {/* Legal links */}
          <Reveal>
            <div className="mx-auto mt-10 flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03] sm:flex-row">
              <div>
                <div className="text-sm font-bold text-slate-900 dark:text-white">Astrid's terms & privacy</div>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">The usage terms and privacy notice governing your use of Astrid.</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Link to="/legal/astrid-terms" data-testid="astrid-terms-link" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-brand hover:text-brand dark:border-white/20 dark:text-white">Terms of Use</Link>
                <Link to="/legal/astrid-privacy" data-testid="astrid-privacy-link" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-brand hover:text-brand dark:border-white/20 dark:text-white">Privacy</Link>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <CTASection />

      <AstridDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} initialQuestion={seedQuestion} />
    </div>
  );
}
